import React, { useEffect, useState } from "react";
import {
  CustomTabPanel,
  Loader,
  PageListLayout,
  InputControl,
  CustomeDialog,
  usePermission,
} from "../../../shared";
import {
  Avatar,
  Tab,
  Tabs,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
  Grid,
  Link,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  BasicDetailsView,
  CostAndPricingView,
  RecipeLabels,
  RecipeReferences,
  RecipeStockItems,
  SuccessDialog,
  TipsAndTricksDetails,
  CostChangeHistory,
} from "../components";
import { HowToPrepareDetails } from "../components";
import { StoreDetails } from "../components/StoreDetails";
import { ChartSettingsView } from "../components";
import { IngredientsTab } from "../../Ingredients/components/IngredientsTab";
import { useLocation, useParams } from "react-router-dom";
import { useCreateOrEdit, useRecipeDetails, useRecipeList } from "../hooks";
import editIcon from "../../../assets/images/Icons/editIcon.png";
import { useNavigate } from "react-router-dom";
import ActiveAndInactiveDialog from "../components/ActiveAndInactiveDialog";
import { useLabelPrinting } from "../../LabelPrinting/hooks/useLabelPrinting";
import copyIcon from "../../../assets/images/Icons/copyImage.svg";
import RecipeCloneDialog from "../components/RecipeCloneDialog";

export const RecipeDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  let { id } = useParams();
  const [search, setSearch] = useState("");
  const {
    optionsTaxGroup,
    ingredients,
    handleSuccessModal,
    alternativeunitList,
  } = useCreateOrEdit({
    list: true,
    isCreate: state?.isCreate,
    recipeId: id,
  });
  const isCostPricing = location.search.includes("cost-pricing");
  const isStockTakeItemsViewable = usePermission("recipe-stock-take-view");

  const {
    ingredientQuery,
    basicDetailsQuery,
    selectStatus,
    updateRecipeStatusMuate,
    isEditable,
    isStatusChange,
    isRecipeTipsAndTricks,
    errorState,
    handleClose,
    globalData,
    handleRecipeDownload,
    downloadRecipe,
    updateOverHead,
    forceInactiveLoading,
    onUpdateRecipeStatusFromTemplates,
  } = useRecipeDetails({ recipeId: id, isDetails: true, isCostPricing });
  const { recipe, onOpenRecipeCloneDialog, handleRecipeClone, recipeClone } =
    useRecipeList({
      recipe: false,
    });
  const { isVisible } = useLabelPrinting({
    isList: true,
    recipeId: id,
    isLabelprintingList: location.search.includes("labels") ? true : false,
  });

  const isContentLoading =
    ingredientQuery.isLoading || basicDetailsQuery.isLoading;

  const breadcrumbs = [
    <Link
      underline="none"
      key="1"
      color="#1760D2"
      onClick={(e) => navigate("/recipe")}
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      Recipe
    </Link>,
    <Typography
      key="2"
      color="inherit"
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      {basicDetailsQuery?.data?.name}
    </Typography>,
  ];

  const handleChange = (event, newValue) => {
    navigate(`/recipe/details/${id}${newValue}`);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.toString().includes("basic" || "status")) {
      setSearch("?basic-details");
    } else if (searchParams.toString().includes("chart-settings")) {
      setSearch("?chart-settings");
    } else if (searchParams.toString().includes("how-to-prepare")) {
      setSearch("?how-to-prepare");
    } else if (searchParams.toString().includes("store")) {
      setSearch("?store");
    } else if (searchParams.toString().includes("cost-pricing")) {
      setSearch("?cost-pricing");
    } else if (searchParams.toString().includes("tips-tricks")) {
      setSearch("?tips-tricks");
    } else if (searchParams.toString().includes("labels")) {
      setSearch("?labels");
    } else if (searchParams.toString().includes("references")) {
      setSearch("?references");
    } else if (searchParams.toString().includes("stock-items")) {
      setSearch("?stock-items");
    } else if (searchParams.toString().includes("cost-change-history")) {
      setSearch("?cost-change-history");
    }
  }, [location.search]);

  // Show loader while content is loading
  if (isContentLoading) return <Loader />;

  return (
    <PageListLayout
      breadcrumbs={breadcrumbs}
      headerText={
        <Stack sx={{ alignItems: "center" }} direction="row" gap="12px">
          <Avatar
            sx={{
              height: "48px",
              width: "48px",
              backgroundColor: "transparent",
            }}
            variant="rounded"
            src={
              basicDetailsQuery?.data?.image
                ? basicDetailsQuery?.data?.image
                : "/static/images/avatar/1.jpg"
            }
          />

          <Typography
            key="3"
            sx={{
              fontSize: "24px",
              fontWeight: 600,
              lineHeight: "29.05px",
              color: "#111C2B",
            }}
          >
            {basicDetailsQuery?.data?.name}
          </Typography>
          <Chip
            size="small"
            color={basicDetailsQuery?.data?.status === 1 ? "primary" : "gray"}
            variant="filled"
            label={
              basicDetailsQuery?.data?.status === 1 ? "ACTIVE" : "INACTIVE"
            }
          />
        </Stack>
      }
      rightContent={
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          {isStatusChange && (
            <InputControl
              type="dropdown"
              options={selectStatus?.statusOptions}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={
                selectStatus?.statusOptions?.find(
                  (option) => option.id === basicDetailsQuery?.data?.status,
                ) || null
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Status" size="small" />
              )}
              onChange={(event, option) =>
                selectStatus.statusValueChangeHandler(option)
              }
              disableClearable
              sx={{
                minWidth: "150px",
              }}
            />
          )}
          <IconButton>
            <Box
              component="img"
              src={copyIcon}
              onClick={(e) => {
                onOpenRecipeCloneDialog(true, id, basicDetailsQuery.data.name);
              }}
            />
          </IconButton>
          {downloadRecipe?.isPending ? (
            <CircularProgress size="30px" color="inherit" />
          ) : (
            <IconButton>
              <img
                alt="details"
                src="/icons/ic_print.png"
                onClick={(e) => {
                  handleRecipeDownload(id);
                }}
              />
            </IconButton>
          )}
          {isEditable && (
            <IconButton>
              <Box
                onClick={(e) => {
                  navigate(`/recipe/edit/${id}`);
                }}
                component="img"
                src={editIcon}
                sx={{ cursor: "pointer" }}
              />
            </IconButton>
          )}
        </Stack>
      }
      tabs={
        <Tabs value={search} onChange={handleChange}>
          <Tab label="Basic Details" value={"?basic-details"} />
          <Tab label="Chart Settings" value={"?chart-settings"} />
          <Tab label="How to Prepare" value={"?how-to-prepare"} />
          <Tab label="Stores" value={"?store"} />
          <Tab label="Cost & Pricing" value={"?cost-pricing"} />
          {isRecipeTipsAndTricks && (
            <Tab label="Tips & Procedures" value={"?tips-tricks"} />
          )}
          {isVisible && <Tab label="Labels" value={"?labels"} />}
          <Tab label="References" value={"?references"} />
          {isStockTakeItemsViewable && (
            <Tab label="Stock Items" value={"?stock-items"} />
          )}
          <Tab label="Cost Change History" value={"?cost-change-history"} />
        </Tabs>
      }
      sx={{
        p: "0px 16px 16px 16px",
        backgroundColor: "background.paper",
        minHeight: "calc(100vh - 145px)",
      }}
    >
      <CustomTabPanel value={search} index={"?basic-details"} border={false}>
        <TabContent
          ingredientQuery={ingredientQuery}
          ingredientGroups={ingredientQuery?.data?.groups}
          component={
            <BasicDetailsView
              basicDetailsQuery={basicDetailsQuery}
              alternative_uom={ingredientQuery?.data?.alternative_uom}
              total_weight={Number(ingredientQuery?.data?.total_weight)}
              total_weight_unit={ingredientQuery?.data?.total_weight_unit}
              selling_unit_name={ingredientQuery?.data?.selling_unit_name}
              selling_unit_symbol={ingredientQuery?.data?.selling_unit_symbol}
              optionsTaxGroup={optionsTaxGroup}
              globalData={globalData}
            />
          }
          globalData={globalData}
        />
      </CustomTabPanel>
      <CustomTabPanel value={search} index={"?chart-settings"} border={false}>
        <TabContent
          ingredientQuery={ingredientQuery}
          ingredientGroups={ingredientQuery?.data?.groups}
          component={<ChartSettingsView recipeId={id} />}
          globalData={globalData}
        />
      </CustomTabPanel>
      <CustomTabPanel value={search} index={"?how-to-prepare"} border={false}>
        <TabContent
          ingredientQuery={ingredientQuery}
          ingredientGroups={ingredientQuery?.data?.groups}
          component={<HowToPrepareDetails recipeId={id} />}
          globalData={globalData}
        />
      </CustomTabPanel>
      <CustomTabPanel value={search} index={"?store"} border={false}>
        <TabContent
          ingredientQuery={ingredientQuery}
          ingredientGroups={ingredientQuery?.data?.groups}
          component={<StoreDetails recipeId={id} />}
          globalData={globalData}
        />
      </CustomTabPanel>
      <CustomTabPanel value={search} index={"?cost-pricing"} border={false}>
        <CostAndPricingView
          recipeId={id}
          ingredientQuery={ingredientQuery}
          basicDetailsQuery={basicDetailsQuery}
          isEditable={isEditable}
          globalData={globalData}
          updateOverHead={updateOverHead}
        />
      </CustomTabPanel>
      <CustomTabPanel value={search} index={"?tips-tricks"} border={false}>
        <TipsAndTricksDetails recipeId={id} />
      </CustomTabPanel>
      <CustomTabPanel value={search} index={"?labels"} border={false}>
        <RecipeLabels recipeId={id} basicDetailsQuery={basicDetailsQuery} />
      </CustomTabPanel>
      <CustomTabPanel value={search} index={"?references"} border={false}>
        <RecipeReferences id={id} />
      </CustomTabPanel>
      <CustomTabPanel
        value={search}
        index={"?cost-change-history"}
        border={false}
      >
        <CostChangeHistory recipeId={id} />
      </CustomTabPanel>
      {isStockTakeItemsViewable && (
        <CustomTabPanel value={search} index={"?stock-items"} border={false}>
          <TabContent
            component={
              <RecipeStockItems
                id={id}
                ingredientGroups={ingredientQuery?.data?.groups || []}
                globalData={globalData}
                alternativeunitList={alternativeunitList}
              />
            }
            hasIngredientTab={false}
          />
        </CustomTabPanel>
      )}

      {/* Recipe cloned successfully */}
      <CustomeDialog
        PaperProps={{
          sx: {
            width: "500px",
          },
        }}
        closeIcon={false}
        open={ingredients.isCreateOpen}
        title={
          <Stack
            direction="row"
            px="24px"
            pt="24px"
            pb="8px"
            gap="8px"
            alignItems="center"
          >
            <img
              src="/icons/ic_success.svg"
              alt="success icon"
              width="36px"
              height="36px"
            />
            <Typography fontWeight={600} fontSize="24px">
              Recipe {ingredients.dialogMessagetype} Successfully!
            </Typography>
          </Stack>
        }
        content={
          <SuccessDialog
            handleClose={(e) => {
              handleSuccessModal(false);
            }}
            onChange={(event, option) =>
              selectStatus.statusValueChangeHandler({ id: 1, name: "Active" })
            }
            disabled={updateRecipeStatusMuate?.isPending}
            messageType={ingredients.dialogMessagetype}
          />
        }
      />

      {/*   Unable to Change Status Dialog */}
      <CustomeDialog
        closeIcon={false}
        open={errorState.isOpen}
        title={
          <Stack direction="row" alignItems="center" p="16px" gap="8px">
            <img
              src="/icons/ic_pair_dialog.svg"
              alt="error icon"
              width="36px"
              height="36px"
            />

            <Typography fontWeight={500} variant="body1" fontSize={20}>
              {errorState.errorData.can_change_status
                ? "Alert"
                : "Unable to Change Status"}
            </Typography>
          </Stack>
        }
        sx={{
          width: { sm: "500px", xs: "90%" },
        }}
        content={
          <ActiveAndInactiveDialog
            errorState={errorState}
            handleClose={handleClose}
          />
        }
        footer={
          errorState.errorData.can_change_status
            ? {
                display: true,
                onCancel: handleClose,
                cancelButton: "No",
                onConfirm: onUpdateRecipeStatusFromTemplates,
                confirmButton: "Yes",
                isDisabled: forceInactiveLoading,
              }
            : {
                display: true,
                onCancel: handleClose,
                cancelButton: "Close",
              }
        }
      />

      {/* Clone Recipe Dialog */}
      <CustomeDialog
        sx={{ width: { sm: "482px", sx: "90%" } }}
        open={recipe.cloneRecipe}
        handleClose={() => onOpenRecipeCloneDialog(false)}
        title="Clone Recipe"
        divider={false}
        content={
          <RecipeCloneDialog
            state={recipe}
            onOpenRecipeCloneDialog={onOpenRecipeCloneDialog}
            handleRecipeClone={handleRecipeClone}
            recipeClone={recipeClone}
          />
        }
      />
    </PageListLayout>
  );
};

const TabContent = ({
  component,
  ingredientGroups,
  globalData,
  ingredientQuery,
  hasIngredientTab = true,
}) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={hasIngredientTab ? 8 : 12}>
        {component}
      </Grid>
      {hasIngredientTab && (
        <Grid item xs={4}>
          <IngredientsTab
            ingredientGroups={ingredientGroups}
            globalData={globalData}
            ingredientQuery={ingredientQuery?.data}
          />
        </Grid>
      )}
    </Grid>
  );
};
